import React, { useEffect, useRef } from "react";
import { useIsMount } from "../../hooks/useIsMount";
import ValidationMessages from "./ValidationMessages.component";
import { ValidationResponse, ObjectAssignedEnum } from "types/validation";
import { useDispatch } from "react-redux";
import { getMainPropertyValidation } from "../../store/reducers/products/properties/pl-properties.actions";
import * as _ from "lodash";
import { getSubPropertyValidation } from "../../store/reducers/products/subproperties/pl-subproperties.actions";
import { useParentPropertyContext } from "../../pages/Products/pages/ProductDetail/ProductLineProperties/ParentPropertyContext";
import { getTableValidation } from "../../store/reducers/products/tables/tables.actions";
import { validationDispatcher } from "./ValidationDispatcher";
import { useValidationBatchCheck } from "./useValidationBatchCheck";

export default function Validator({
  objectType,
  validations,
  objectData = {},
  isStandardSubProperty = false,
  isSubProperty = false,
  readOnly = false,
}: {
  objectType: ObjectAssignedEnum;
  validations: ValidationResponse[];
  objectData?: any;
  isStandardSubProperty?: boolean;
  isSubProperty?: boolean;
  readOnly?: boolean;
}) {
  const dispatch = useDispatch();

  const isMount = useIsMount();

  const { parentId } = useParentPropertyContext();

  //We will check to see if the objectData has changed (ignoring any validations)
  //and only change the reference (and therefore fetch new validations) if data has changed
  //Ignoring validations allows this to not fetch validations twice.
  //The first data change from the users change causes validations to fetch, but
  //then the validation changing would be seen as a change to refetch validations again.
  //We don't want this duplicate call.
  const objectDataRef = useRef(objectData);
  if (
    !_.isEqual(
      _.omit(objectDataRef.current, ["validations"]),
      _.omit(objectData, ["validations"])
    )
  ) {
    objectDataRef.current = objectData;
  }

  const validationBatchCheck = useValidationBatchCheck({ objectData });
  const validationBatchCheckRef = useRef(validationBatchCheck);
  if (!_.isEqual(validationBatchCheckRef.current, validationBatchCheck)) {
    validationBatchCheckRef.current = validationBatchCheck;
  }

  useEffect(() => {
    validationDispatcher(
      dispatch,
      objectType,
      objectData.id,
      validationBatchCheck,
      validations
    );
  }, [validationBatchCheckRef.current]);

  // fetch validation based on assigned object
  useEffect(() => {
    if (isMount) return;
    if (readOnly) return;

    if (objectType === ObjectAssignedEnum.RULETABLE) {
      setTimeout(() => {
        dispatch(
          getTableValidation({
            objectId: objectData.id,
            objectAssigned: ObjectAssignedEnum.RULETABLE,
          })
        );
      }, 2000);
      return;
    }

    if (isSubProperty) {
      setTimeout(() => {
        dispatch(
          getSubPropertyValidation({
            parentId,
            objectId: objectData.id,
            objectAssigned: objectType,
          })
        );
      }, 2000);
      return;
    }

    if (objectType === ObjectAssignedEnum.PROPERTY && !isStandardSubProperty) {
      setTimeout(() => {
        dispatch(
          getMainPropertyValidation({
            objectId: objectData.id,
            objectAssigned: objectType,
          })
        );
      }, 2000);
      return;
    }

    if (isStandardSubProperty) {
      setTimeout(() => {
        dispatch(
          getSubPropertyValidation({
            parentId,
            objectId: objectData.id,
            objectAssigned: objectType,
            isStandardSubProperty,
          })
        );
      }, 2000);
      return;
    }
  }, [objectDataRef.current]);

  return <ValidationMessages validations={validations} />;
}
